import { Injectable } from "@angular/core";
import { LeaveRequest, LeaveRequestsData } from "../interfaces/leave-request";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { HttpParams } from "@angular/common/http";
import { ServerDataSource } from "angular2-smart-table";
import { environment } from "../../../environments/environment";
import { HttpService } from "./common/http.service";
import { NotificationService } from './notification.service';
import { User } from "../interfaces/auth/users";
import { omit } from "lodash";

@Injectable()
export class LeaveRequestsService extends LeaveRequestsData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true`);
  }

  constructor(private api: HttpService, private notificationService: NotificationService) {
    super();
    this.url = 'leave-requests';
  }

  getDataSourceOfClient(clientId: number): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?with=deep,2&clients_include_id=${clientId}`);
  }

  listAll(): Observable<LeaveRequest[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0')
      .append('with', 'deep,2');

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  list(pageNumber: number, pageSize: number, query: string = ''): Observable<LeaveRequest[]> {
    let params: HttpParams = new HttpParams()
      .append('with', 'deep,2');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    if (query) {
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  rawQry(pageNumber: number, pageSize: number, params: HttpParams):Observable<LeaveRequest[] | any> {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  get(id: any): Observable<LeaveRequest> {
    return this.api.get(`${this.url}/${id}`, { params: new HttpParams().append('populate', '*') })
      .pipe(map(res => res.data || res));
  }

  create(LeaveRequest: LeaveRequest): Observable<LeaveRequest> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    return this.api.post(this.url, { data: LeaveRequest }, params)
      .pipe(map(res => res.data));
  }

  update(LeaveRequest: LeaveRequest): Observable<LeaveRequest> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    
    // Use id if documentId is not available
    const leaveId = LeaveRequest.documentId || (LeaveRequest as any).id;
    if (!leaveId) {
      console.error('Leave has no id or documentId:', LeaveRequest);
      return new Observable(observer => observer.error(new Error('Leave record has no valid ID')));
    }

    // Only send the fields that should be updated, excluding nested objects and system fields
    const changedLeaveRequest: any = {
      leaveStatus: LeaveRequest.leaveStatus,
    };
    
    // If person is an object with a documentId or id, send just the ID
    if (LeaveRequest.person) {
      changedLeaveRequest.person = LeaveRequest.person.documentId || (LeaveRequest.person as any).id || LeaveRequest.person;
    }
    
    // Include other fields if they're primitives
    if (LeaveRequest.type) changedLeaveRequest.type = LeaveRequest.type;
    if (LeaveRequest.startDate) changedLeaveRequest.startDate = LeaveRequest.startDate;
    if (LeaveRequest.endDate) changedLeaveRequest.endDate = LeaveRequest.endDate;
    if (LeaveRequest.reason) changedLeaveRequest.reason = LeaveRequest.reason;

    console.log('Sending update payload:', changedLeaveRequest);
    
    return this.api.put(`${this.url}/${leaveId}`, { data: changedLeaveRequest }, params)
      .pipe(
        map(res => res.data),
        tap((updated: LeaveRequest) => {
          try {
            if (updated?.leaveStatus && updated.leaveStatus.toLowerCase() === 'approved') {
              const empId = updated.person?.documentId || (updated.person as any)?.id;
              const employeeName = updated.person ? `${updated.person.firstName || ''} ${updated.person.lastName || ''}`.trim() : 'Employee';
              const leaveRange = (updated.startDate && updated.endDate) ? `${updated.startDate} — ${updated.endDate}` : undefined;
              this.notificationService.notifyEmployeeOfLeaveApproval(empId, employeeName, leaveRange);
            }
          } catch (e) {
            // Non-fatal: ignore notification failures
          }
        })
      );
  }

  archive(LeaveRequest: LeaveRequest, date: Date, user: User): Observable<LeaveRequest> {
    return this.api.put(`${this.url}/${LeaveRequest.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }

  unarchive(LeaveRequest: LeaveRequest): Observable<LeaveRequest> {
    return this.api.put(`${this.url}/${LeaveRequest.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }

  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }

}